import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  MenuItem,
  Select,
  Typography,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  InputBase,
  Badge,
  Tooltip,
  Menu,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import Layout from "../../layout/Layout";
import {
  config,
  purchaseOrderAPI,
  projectMasterAPI,
  vendorAPI,
  manageCompaniesAPI,
} from "../../config";

// Icons
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BusinessIcon from "@mui/icons-material/Business";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/GetApp";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

function PurchaseOrderDashboard() {
  const [loading, setLoading] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);

  // Dropdown list states
  const [projectList, setProjectList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [companyList, setCompanyList] = useState([]);

  // Filter states
  const [selectedProject, setSelectedProject] = useState("All");
  const [selectedVendor, setSelectedVendor] = useState("All");
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [dateFilter, setDateFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Notifications (commented out)
  // const [notifAnchor, setNotifAnchor] = useState(null);
  // const [notifications, setNotifications] = useState([]);

  // Load static filter options
  useEffect(() => {
    axios({ ...config, method: "POST", data: JSON.stringify({ intType: 6 }), url: projectMasterAPI })
      .then((res) => setProjectList(res.data.result || []))
      .catch((err) => console.error("Projects fetch failed", err));

    axios({ ...config, method: "POST", data: JSON.stringify({ intType: 3 }), url: vendorAPI })
      .then((res) => setVendorList(res.data.result || []))
      .catch((err) => console.error("Vendors fetch failed", err));

    axios({ ...config, method: "POST", data: JSON.stringify({ intType: 5 }), url: manageCompaniesAPI })
      .then((res) => setCompanyList(res.data.result || []))
      .catch((err) => console.error("Companies fetch failed", err));
  }, []);

  // Fetch PO / WO list
  const fetchAllData = () => {
    setLoading(true);
    const requestData = JSON.stringify({
      txtOrderType: 0,
      txtPODate: "2000-2050",
      txtCompanyName: 0,
      txtSearchString: "",
      txtSortDirection: "desc",
      intLimit: 100000,
      intOffset: 0,
      intType: 23,
    });

    axios({ ...config, method: "POST", data: requestData, url: purchaseOrderAPI })
      .then((res) => setAllOrders(res.data.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...allOrders];

    if (selectedProject !== "All") {
      filtered = filtered.filter((o) => o.project_name === selectedProject);
    }
    if (selectedVendor !== "All") {
      filtered = filtered.filter((o) => o.vendor_name === selectedVendor);
    }
    if (selectedCompany !== "All") {
      filtered = filtered.filter((o) => o.company_Name === selectedCompany);
    }

    if (dateFilter !== "All") {
      const today = moment();
      filtered = filtered.filter((o) => {
        if (!o.po_date) return false;
        const poDate = moment(o.po_date);
        if (dateFilter === "Today") return poDate.isSame(today, "day");
        if (dateFilter === "This Week") return poDate.isSame(today, "week");
        if (dateFilter === "This Month") return poDate.isSame(today, "month");
        if (dateFilter === "This Year") return poDate.isSame(today, "year");
        return true;
      });
    }

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          (o.po_no && o.po_no.toLowerCase().includes(query)) ||
          (o.project_name && o.project_name.toLowerCase().includes(query)) ||
          (o.vendor_name && o.vendor_name.toLowerCase().includes(query))
      );
    }

    setFilteredOrders(filtered);
  }, [allOrders, selectedProject, selectedVendor, selectedCompany, dateFilter, searchQuery]);

  // Notifications init (commented out)
  // useEffect(() => {
  //   if (allOrders.length > 0 && notifications.length === 0) {
  //     const initialNotifs = allOrders
  //       .filter((o) => o.status !== 100 && o.po_no)
  //       .sort((a, b) => new Date(b.po_date) - new Date(a.po_date))
  //       .slice(0, 10)
  //       .map((o, idx) => ({
  //         id: o.id || `${o.po_no}-${idx}`,
  //         po_no: o.po_no,
  //         time: o.po_date ? moment(o.po_date).format("MMM DD, YYYY") : "Just now",
  //         type: Number(o.order_type) === 2 ? "WO Initiated" : "PO Created",
  //         desc: `${Number(o.order_type) === 2 ? "Work" : "Purchase"} order ${o.po_no || ""} generated for ${o.vendor_name || ""}`,
  //       }));
  //     setNotifications(initialNotifs);
  //   }
  // }, [allOrders, notifications.length]);

  // const handleNotifClick = (notif) => {
  //   if (notif.po_no) {
  //     setSearchQuery(notif.po_no);
  //   }
  //   setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
  //   setNotifAnchor(null);
  // };

  // Metrics (PO and WO values and counts)
  const metrics = useMemo(() => {
    let poValue = 0;
    let woValue = 0;
    let poCount = 0;
    let woCount = 0;
    const uniqueProjects = new Set();

    filteredOrders.forEach((o) => {
      const val = Number(o.total_with_gst || 0);
      if (o.status !== 100) {
        if (Number(o.order_type) === 1) {
          poValue += val;
          poCount++;
        } else if (Number(o.order_type) === 2) {
          woValue += val;
          woCount++;
        }
        if (o.project_name) uniqueProjects.add(o.project_name);
      }
    });

    const totalValue = poValue + woValue;
    const totalCount = poCount + woCount;
    const avgOrderValue = totalCount > 0 ? totalValue / totalCount : 0;

    return {
      totalValue,
      totalCount,
      poValue,
      woValue,
      poCount,
      woCount,
      activeProjects: uniqueProjects.size,
      avgOrderValue,
    };
  }, [filteredOrders]);

  const formatCr = (val) => {
    return `₹ ${(val / 10000000).toFixed(2)} Cr`;
  };

  const formatAmount = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  // PO & WO Value Trend
  const trendData = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      months.push(moment().subtract(i, "months").format("MMM YYYY"));
    }
    const poVals = Array(6).fill(0);
    const woVals = Array(6).fill(0);
    const orderCounts = Array(6).fill(0);

    filteredOrders.forEach((o) => {
      if (o.status === 100 || !o.po_date) return;
      const poMonth = moment(o.po_date).format("MMM YYYY");
      const idx = months.indexOf(poMonth);
      if (idx !== -1) {
        const val = Number(o.total_with_gst || 0);
        orderCounts[idx]++;
        if (Number(o.order_type) === 1) {
          poVals[idx] += val;
        } else if (Number(o.order_type) === 2) {
          woVals[idx] += val;
        }
      }
    });

    return {
      categories: months,
      poValues: poVals.map((v) => Number((v / 10000000).toFixed(2))),
      woValues: woVals.map((v) => Number((v / 10000000).toFixed(2))),
      counts: orderCounts,
    };
  }, [filteredOrders]);

  // Project Breakdown Top 6
  const projectBreakdown = useMemo(() => {
    const pMap = {};
    filteredOrders.forEach((o) => {
      if (o.status === 100) return;
      const p = o.project_name || "Others";
      pMap[p] = (pMap[p] || 0) + Number(o.total_with_gst || 0);
    });

    const sorted = Object.entries(pMap)
      .map(([name, val]) => ({ name, val: Number((val / 10000000).toFixed(2)) }))
      .sort((a, b) => b.val - a.val)
      .slice(0, 6);

    return {
      names: sorted.map((s) => s.name),
      values: sorted.map((s) => s.val),
    };
  }, [filteredOrders]);

  // Category Distribution
  const categoryBreakdown = useMemo(() => {
    const catMap = {
      "Steel & Structural": 0,
      "Cement & Concrete": 0,
      "Electrical": 0,
      "HVAC & Plumbing": 0,
      "Finishes": 0,
      "Others": 0,
    };
    filteredOrders.forEach((o) => {
      if (o.status === 100) return;
      const val = Number(o.total_with_gst || 0);
      const nameStr = `${o.project_name} ${o.vendor_name}`.toLowerCase();
      if (nameStr.includes("steel") || nameStr.includes("metal") || nameStr.includes("iron")) {
        catMap["Steel & Structural"] += val;
      } else if (nameStr.includes("cement") || nameStr.includes("concrete") || nameStr.includes("sand")) {
        catMap["Cement & Concrete"] += val;
      } else if (nameStr.includes("electric") || nameStr.includes("cable") || nameStr.includes("wire")) {
        catMap["Electrical"] += val;
      } else if (nameStr.includes("plumb") || nameStr.includes("pipe") || nameStr.includes("ac") || nameStr.includes("hvac")) {
        catMap["HVAC & Plumbing"] += val;
      } else if (nameStr.includes("paint") || nameStr.includes("tile") || nameStr.includes("finish")) {
        catMap["Finishes"] += val;
      } else {
        catMap["Others"] += val;
      }
    });

    return {
      labels: Object.keys(catMap),
      values: Object.values(catMap).map((v) => Number((v / 10000000).toFixed(2))),
    };
  }, [filteredOrders]);

  // Top 5 Vendors
  const topVendors = useMemo(() => {
    const vMap = {};
    filteredOrders.forEach((o) => {
      if (o.status === 100) return;
      const v = o.vendor_name || "Unknown Vendor";
      vMap[v] = (vMap[v] || 0) + Number(o.total_with_gst || 0);
    });

    const totalAll = Object.values(vMap).reduce((sum, val) => sum + val, 0);

    return Object.entries(vMap)
      .map(([name, val]) => ({
        name,
        valCr: (val / 10000000).toFixed(2),
        pct: totalAll > 0 ? ((val / totalAll) * 100).toFixed(1) + "%" : "0%",
      }))
      .sort((a, b) => Number(b.valCr) - Number(a.valCr))
      .slice(0, 5);
  }, [filteredOrders]);

  // Recent POs
  const recentPOs = useMemo(() => {
    return [...filteredOrders]
      .filter((o) => Number(o.order_type) === 1 && o.status !== 100)
      .sort((a, b) => new Date(b.po_date) - new Date(a.po_date))
      .slice(0, 5);
  }, [filteredOrders]);

  // Recent WOs
  const recentWOs = useMemo(() => {
    return [...filteredOrders]
      .filter((o) => Number(o.order_type) === 2 && o.status !== 100)
      .sort((a, b) => new Date(b.po_date) - new Date(a.po_date))
      .slice(0, 5);
  }, [filteredOrders]);

  // Export to Excel
  const handleExportExcel = () => {
    const dataToExport = filteredOrders.map((o) => ({
      "Order Number": o.po_no,
      Date: o.po_date ? moment(o.po_date).format("YYYY-MM-DD") : "-",
      Type: Number(o.order_type) === 1 ? "Purchase Order" : "Work Order",
      Project: o.project_name,
      Vendor: o.vendor_name,
      "Total Value": Number(o.total_with_gst || 0),
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "Procurement_Ledger.xlsx");
  };

  const handleExportPDF = () => {
    window.print();
  };

  return (
    <Layout>
      <Box sx={{ padding: "20px 25px", pt: "95px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        {/* HEADER SECTION */}
        <Grid container spacing={2} sx={{ mb: 4 }} alignItems="center">
          <Grid item xs={12} lg={4}>
            <Box>
              <Typography variant="h5" fontWeight="800" color="#0f172a">
                Purchase Order & Work Order Dashboard
              </Typography>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 0.5, "& .MuiBreadcrumbs-li": { fontSize: "11px" } }}>
                <Link underline="hover" color="inherit" href="/dashboard">Dashboard</Link>
                <Typography color="text.primary" fontSize="11px">PO & WO Analytics</Typography>
              </Breadcrumbs>
            </Box>
          </Grid>

          {/* Quick Search */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "30px", px: 2, py: 0.5, boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
              <SearchIcon sx={{ color: "#64748b", mr: 1, fontSize: 18 }} />
              <InputBase
                placeholder="Search PO No., Project, Vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ fontSize: "12px", width: "100%" }}
              />
            </Box>
          </Grid>

          {/* Action buttons */}
          <Grid item xs={12} lg={4} sx={{ display: "flex", gap: "10px", justifyContent: { xs: "flex-start", lg: "flex-end" }, alignItems: "center" }}>
            {/* Notification bell commented out
            <IconButton onClick={(e) => setNotifAnchor(e.currentTarget)} sx={{ border: "1px solid #e2e8f0", background: "#fff" }}>
              <Badge badgeContent={notifications.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            */}

            <Button variant="contained" onClick={handleExportExcel} startIcon={<DownloadIcon />} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: "600", fontSize: "12px", background: "linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)" }}>
              Export Excel
            </Button>

            <Button variant="outlined" onClick={handleExportPDF} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: "600", fontSize: "12px", color: "#0f172a", borderColor: "#64748b", background: "#fff" }}>
              Print Report
            </Button>
          </Grid>
        </Grid>

        {/* Global Filter Bar */}
        <Box sx={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "16px", p: 2, mb: 4, boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "600", mb: 0.5, display: "block" }}>Date Period</Typography>
              <Select fullWidth size="small" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} sx={{ height: "35px", fontSize: "12px", borderRadius: "8px" }}>
                <MenuItem value="All">All Time</MenuItem>
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="This Week">This Week</MenuItem>
                <MenuItem value="This Month">This Month</MenuItem>
                <MenuItem value="This Year">This Year</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "600", mb: 0.5, display: "block" }}>Project Portfolio</Typography>
              <Select fullWidth size="small" value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} sx={{ height: "35px", fontSize: "12px", borderRadius: "8px" }}>
                <MenuItem value="All">All Projects</MenuItem>
                {projectList.map((p) => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "600", mb: 0.5, display: "block" }}>Primary Vendor</Typography>
              <Select fullWidth size="small" value={selectedVendor} onChange={(e) => setSelectedVendor(e.target.value)} sx={{ height: "35px", fontSize: "12px", borderRadius: "8px" }}>
                <MenuItem value="All">All Vendors</MenuItem>
                {vendorList.map((v) => (
                  <MenuItem key={v.id} value={v.name}>{v.name}</MenuItem>
                ))}
              </Select>
            </Grid>

            {/* <Grid item xs={6} sm={4} md={2.4}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "600", mb: 0.5, display: "block" }}>Business Entity</Typography>
              <Select fullWidth size="small" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} sx={{ height: "35px", fontSize: "12px", borderRadius: "8px" }}>
                <MenuItem value="All">All Companies</MenuItem>
                {companyList.map((c) => (
                  <MenuItem key={c.company_Id} value={c.company_Name}>{c.company_Name}</MenuItem>
                ))}
              </Select>
            </Grid> */}

            <Grid item xs={12} sm={4} md={2.4} display="flex" alignItems="flex-end">
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setSelectedProject("All");
                  setSelectedVendor("All");
                  setSelectedCompany("All");
                  setDateFilter("All");
                  setSearchQuery("");
                  fetchAllData();
                }}
                startIcon={<RefreshIcon />}
                sx={{ height: "35px", textTransform: "none", borderRadius: "8px", borderColor: "#64748b", color: "#0f172a", whiteSpace: "nowrap" }}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* EXECUTIVE KPI CARDS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">TOTAL PO VALUE</Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 0.5 }}>{formatCr(metrics.poValue)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(37,99,235,0.1)", color: "#2563eb" }}>
                  <FolderOpenIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#10b981", display: "flex", alignItems: "center", gap: 0.5, mt: 1.5, fontWeight: "700" }}>
                <TrendingUpIcon fontSize="inherit" /> {metrics.poCount} POs Generated
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">TOTAL WO VALUE</Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 0.5 }}>{formatCr(metrics.woValue)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(168,85,247,0.1)", color: "#a855f7" }}>
                  <LocalShippingIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#a855f7", display: "flex", alignItems: "center", gap: 0.5, mt: 1.5, fontWeight: "700" }}>
                <TrendingUpIcon fontSize="inherit" /> {metrics.woCount} WOs Generated
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">COMBINED SPEND</Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 0.5 }}>{formatCr(metrics.totalValue)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(16,185,129,0.1)", color: "#10b981" }}>
                  <ShoppingCartIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1.5, fontWeight: "600" }}>
                Avg Order Size: {formatAmount(metrics.avgOrderValue)}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">ACTIVE PROJECTS</Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 0.5 }}>{metrics.activeProjects}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}>
                  <BusinessIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1.5, fontWeight: "600" }}>
                Across {metrics.totalCount} total orders
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* CHARTS ROW */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* PO vs WO spend trend */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Monthly PO & WO Spend Trend (₹ Cr)
              </Typography>
              <ReactApexChart
                options={{
                  chart: { type: "bar", toolbar: { show: false } },
                  plotOptions: { bar: { horizontal: false, columnWidth: "50%", borderRadius: 4 } },
                  dataLabels: { enabled: false },
                  grid: { borderColor: "#f1f5f9" },
                  colors: ["#2563eb", "#a855f7"],
                  xaxis: { categories: trendData.categories },
                  yaxis: { title: { text: "Value (₹ Cr)" } },
                }}
                series={[
                  { name: "Purchase Orders (PO)", data: trendData.poValues },
                  { name: "Work Orders (WO)", data: trendData.woValues },
                ]}
                type="bar"
                height={280}
              />
            </Card>
          </Grid>

          {/* Category distribution */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 3 }}>
                PO Value by Category
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ flexGrow: 1 }}>
                <ReactApexChart
                  options={{
                    chart: { type: "donut" },
                    labels: categoryBreakdown.labels,
                    colors: ["#334155", "#64748b", "#3b82f6", "#f97316", "#10b981", "#8b5cf6"],
                    plotOptions: { pie: { donut: { size: "65%" } } },
                    legend: { position: "bottom" },
                  }}
                  series={categoryBreakdown.values}
                  type="donut"
                  height={250}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* PROJECT & VENDORS ROW */}
        <Grid
          container
          spacing={3}
          sx={{ mb: 4 }}
          alignItems="stretch"
        >
          {/* Project breakdown */}
          <Grid
            item
            xs={12}
            lg={7}
            sx={{
              display: "flex",
              "@media (min-width: 1440px)": {
                paddingTop: "64px !important",
              },
            }}
          >
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", width: "100%", display: "flex", marginTop: "32px", flexDirection: "column", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Order Value by Project (Top 6)
              </Typography>
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                <ReactApexChart
                  options={{
                    chart: { type: "bar", toolbar: { show: false } },
                    plotOptions: { bar: { horizontal: true, barHeight: "50%", borderRadius: 4 } },
                    dataLabels: { enabled: false },
                    xaxis: { categories: projectBreakdown.names },
                    colors: ["#3b82f6"],
                    tooltip: { y: { formatter: (val) => `${val} Cr` } },
                  }}
                  series={[{ name: "Value (₹ Cr)", data: projectBreakdown.values }]}
                  type="bar"
                  height={260}
                  style={{ width: "100%" }}
                />
              </Box>
            </Card>
          </Grid>

          {/* Top Vendors Table */}
          <Grid
            item
            xs={12}
            lg={5}
            // sx={{
            //   display: "flex",
            //   "@media (min-width: 1440px)": {
            //     marginTop:"64px !important",
            //   },
            // }}
          >
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", width: "100%", display: "flex", marginTop: "32px", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Top 5 Vendors by Order Value
              </Typography>
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Vendor</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "700", py: 1 }}>Value (₹ Cr)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "700", py: 1 }}>% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topVendors.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>{row.name}</TableCell>
                        <TableCell align="right" sx={{ py: 1, fontWeight: "600" }}>₹ {row.valCr} Cr</TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>{row.pct}</TableCell>
                      </TableRow>
                    ))}
                    {topVendors.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 2 }}>No vendor records found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* LEDGER TABLES (RECENT PO & Recent WO) */}
        <Grid container spacing={3}>
          {/* Recent POs */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Recent Purchase Orders (PO)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ background: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>PO No.</TableCell>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Vendor</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "700", py: 1 }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentPOs.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ py: 1 }}>{row.po_no}</TableCell>
                        <TableCell sx={{ py: 1 }}>{row.po_date ? moment(row.po_date).format("DD-MM-YYYY") : "-"}</TableCell>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>{row.project_name}</TableCell>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>{row.vendor_name}</TableCell>
                        <TableCell align="right" sx={{ py: 1, fontWeight: "600" }}>{formatAmount(row.total_with_gst)}</TableCell>
                      </TableRow>
                    ))}
                    {recentPOs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 2 }}>No recent POs</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Recent WOs */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Recent Work Orders (WO)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ background: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>WO No.</TableCell>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Vendor</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "700", py: 1 }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentWOs.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ py: 1 }}>{row.po_no}</TableCell>
                        <TableCell sx={{ py: 1 }}>{row.po_date ? moment(row.po_date).format("DD-MM-YYYY") : "-"}</TableCell>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>{row.project_name}</TableCell>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>{row.vendor_name}</TableCell>
                        <TableCell align="right" sx={{ py: 1, fontWeight: "600" }}>{formatAmount(row.total_with_gst)}</TableCell>
                      </TableRow>
                    ))}
                    {recentWOs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 2 }}>No recent WOs</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* Notifications Menu commented out
        <Menu
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={() => setNotifAnchor(null)}
          PaperProps={{
            sx: {
              background: "#fff",
              color: "#0f172a",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
              width: 320,
              p: 1,
            },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: 1, borderBottom: "1px solid #f1f5f9" }}>
            <Typography variant="subtitle2" fontWeight="700">
              Notifications ({notifications.length})
            </Typography>
            <Box display="flex" gap="4px">
              <Button size="small" onClick={() => setNotifications([])} sx={{ fontSize: "10px", textTransform: "none", py: 0.2 }}>
                Clear All
              </Button>
              <Button size="small" onClick={() => setNotifications([])} sx={{ fontSize: "10px", textTransform: "none", py: 0.2 }}>
                Mark Read
              </Button>
            </Box>
          </Box>
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {notifications.map((act) => (
              <Box
                key={act.id}
                onClick={() => handleNotifClick(act)}
                sx={{
                  p: 1.5,
                  borderBottom: "1px solid #f1f5f9",
                  cursor: "pointer",
                  "&:hover": { background: "rgba(0,0,0,0.02)" }
                }}
              >
                <Typography variant="caption" fontWeight="bold" color="#2563eb">{act.type}</Typography>
                <Typography variant="body2" sx={{ fontSize: "11px", mt: 0.5 }}>{act.desc}</Typography>
                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "9px", mt: 0.5, display: "block" }}>{act.time}</Typography>
              </Box>
            ))}
            {notifications.length === 0 && (
              <Typography variant="body2" sx={{ p: 2, textAlign: "center", color: "#64748b" }}>
                No notifications
              </Typography>
            )}
          </Box>
        </Menu>
        */}
      </Box>
    </Layout>
  );
}

export default PurchaseOrderDashboard;
